import React, { useState } from 'react';
import { X, Check } from 'lucide-react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useSocialMediaSystem, SocialNetwork } from '../../hooks/useSocialMediaSystem';
import { useDesignColors } from '../../hooks/useDesignColors';

interface SocialNetworkFormProps {
  network?: SocialNetwork | null;
  onClose: () => void;
}

export function SocialNetworkForm({ network, onClose }: SocialNetworkFormProps) {
  const { chatbotPublicId } = useChatbot();
  const { addNetwork, updateNetwork } = useSocialMediaSystem();
  const { colors } = useDesignColors();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: network?.type || 'instagram',
    name: network?.name || '',
    url: network?.url || '',
    enabled: network?.enabled ?? true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatbotPublicId) return;
    
    setLoading(true);
    try {
      if (network) {
        // Update existing network
        await updateNetwork(chatbotPublicId, network.id, formData);
      } else {
        // Add new network
        await addNetwork(chatbotPublicId, formData);
      }
      onClose();
    } catch (err) {
      console.error('Error saving social network:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-gray-900/95 rounded-xl border border-white/10 w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white">
            {network ? 'Edit Social Network' : 'Add Social Network'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Network Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
              <option value="linkedin">LinkedIn</option>
              <option value="facebook">Facebook</option>
              <option value="snapchat">Snapchat</option>
              <option value="pinterest">Pinterest</option>
              <option value="spotify">Spotify</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="discord">Discord</option>
              <option value="messenger">Messenger</option>
              <option value="twitter">X (Twitter)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Display Name (optional)
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., Our Instagram (leave empty to show only icon)"
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave empty to display only the icon without text
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              URL
            </label>
            <input
              type="text"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., https://instagram.com/youraccount"
              required
            />
          </div>

          <div className="flex items-center">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="rounded border-white/10 bg-white/5 text-indigo-500 focus:ring-indigo-500"
              />
              <span className="text-gray-300">Enabled</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.url}
              className="px-4 py-2 rounded-lg text-white transition-all flex items-center gap-2"
              style={{
                background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                opacity: loading || !formData.url ? 0.7 : 1
              }}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {network ? 'Update' : 'Add'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}